import React, { useState, useEffect } from "react";
import { Box, Button } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  Search,
  MapPin,
  Download,
  Database,
  Building2,
  HardHat,
  Compass,
  ChevronDown,
  Factory,
} from "lucide-react";
import ProfilePicture1 from "pictures/profile_picture_1_recruiter.jpg";
import ProfilePicture2 from "pictures/profile_picture_2_recruiter.jpg";
import ProfilePicture3 from "pictures/profile_picture_3_recruiter.jpg";
import ProfilePicture4 from "pictures/profile_picture_4_recruiter.png";
import ProfilePicture5 from "pictures/profile_picture_5_recruiter.png";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";

const sectorIcons = [Compass, Building2, HardHat, Factory];

const formatNumber = (n) => n.toLocaleString("fr-BE");

const ProfessionsGrid = () => {
  const i18nLocale = useSelector((state) => state.i18nLocale);
  const intl = useIntl();

  const sectors = [
    {
      name: intl.formatMessage({ id: "landing_recruiter.talent_showcase.sector_architecture" }),
      professions: [
        intl.formatMessage({ id: "landing_recruiter.talent_showcase.arch_residential" }),
        intl.formatMessage({ id: "landing_recruiter.talent_showcase.arch_urbanism" }),
        intl.formatMessage({ id: "landing_recruiter.talent_showcase.arch_interior" }),
        intl.formatMessage({ id: "landing_recruiter.talent_showcase.arch_sustainable" }),
        intl.formatMessage({ id: "landing_recruiter.talent_showcase.arch_health" }),
        intl.formatMessage({ id: "landing_recruiter.talent_showcase.arch_education" }),
        intl.formatMessage({ id: "landing_recruiter.talent_showcase.arch_heritage" }),
        intl.formatMessage({ id: "landing_recruiter.talent_showcase.arch_hospitality" }),
        intl.formatMessage({ id: "landing_recruiter.talent_showcase.arch_landscape" }),
        intl.formatMessage({ id: "landing_recruiter.talent_showcase.arch_bim" }),
      ],
    },
    {
      name: intl.formatMessage({ id: "landing_recruiter.talent_showcase.sector_engineering" }),
      professions: [
        intl.formatMessage({ id: "landing_recruiter.talent_showcase.eng_civil" }),
        intl.formatMessage({ id: "landing_recruiter.talent_showcase.eng_infrastructure" }),
        intl.formatMessage({ id: "landing_recruiter.talent_showcase.eng_mep" }),
        intl.formatMessage({ id: "landing_recruiter.talent_showcase.eng_industrial" }),
        intl.formatMessage({ id: "landing_recruiter.talent_showcase.eng_geotechnical" }),
        intl.formatMessage({ id: "landing_recruiter.talent_showcase.eng_environmental" }),
        intl.formatMessage({ id: "landing_recruiter.talent_showcase.eng_acoustics" }),
        intl.formatMessage({ id: "landing_recruiter.talent_showcase.eng_facade" }),
        intl.formatMessage({ id: "landing_recruiter.talent_showcase.eng_railway" }),
        intl.formatMessage({ id: "landing_recruiter.talent_showcase.eng_fire_safety" }),
        intl.formatMessage({ id: "landing_recruiter.talent_showcase.eng_timber" }),
        intl.formatMessage({ id: "landing_recruiter.talent_showcase.eng_nuclear" }),
        intl.formatMessage({ id: "landing_recruiter.talent_showcase.eng_port" }),
        intl.formatMessage({ id: "landing_recruiter.talent_showcase.eng_tunnels" }),
      ],
    },
    {
      name: intl.formatMessage({ id: "landing_recruiter.talent_showcase.sector_construction" }),
      professions: [
        intl.formatMessage({ id: "landing_recruiter.talent_showcase.con_residential" }),
        intl.formatMessage({ id: "landing_recruiter.talent_showcase.con_civil_infra" }),
        intl.formatMessage({ id: "landing_recruiter.talent_showcase.con_commercial" }),
        intl.formatMessage({ id: "landing_recruiter.talent_showcase.con_metallic" }),
        intl.formatMessage({ id: "landing_recruiter.talent_showcase.con_timber" }),
        intl.formatMessage({ id: "landing_recruiter.talent_showcase.con_specialized" }),
        intl.formatMessage({ id: "landing_recruiter.talent_showcase.con_modular" }),
        intl.formatMessage({ id: "landing_recruiter.talent_showcase.con_renovation" }),
        intl.formatMessage({ id: "landing_recruiter.talent_showcase.con_demolition" }),
        intl.formatMessage({ id: "landing_recruiter.talent_showcase.con_hospital" }),
        intl.formatMessage({ id: "landing_recruiter.talent_showcase.con_underground" }),
        intl.formatMessage({ id: "landing_recruiter.talent_showcase.con_outdoor" }),
      ],
    },
    {
      name: intl.formatMessage({ id: "landing_recruiter.talent_showcase.sector_industry" }),
      professions: [
        intl.formatMessage({ id: "landing_recruiter.talent_showcase.ind_materials" }),
        intl.formatMessage({ id: "landing_recruiter.talent_showcase.ind_equipment" }),
        intl.formatMessage({ id: "landing_recruiter.talent_showcase.ind_metal_fabrication" }),
        intl.formatMessage({ id: "landing_recruiter.talent_showcase.ind_chemistry" }),
        intl.formatMessage({ id: "landing_recruiter.talent_showcase.ind_energy" }),
        intl.formatMessage({ id: "landing_recruiter.talent_showcase.ind_aerospace" }),
        intl.formatMessage({ id: "landing_recruiter.talent_showcase.ind_food_pharma" }),
        intl.formatMessage({ id: "landing_recruiter.talent_showcase.ind_automotive" }),
        intl.formatMessage({ id: "landing_recruiter.talent_showcase.ind_robotics" }),
        intl.formatMessage({ id: "landing_recruiter.talent_showcase.ind_water_treatment" }),
        intl.formatMessage({ id: "landing_recruiter.talent_showcase.ind_recycling" }),
        intl.formatMessage({ id: "landing_recruiter.talent_showcase.ind_logistics" }),
        intl.formatMessage({ id: "landing_recruiter.talent_showcase.ind_naval" }),
        intl.formatMessage({ id: "landing_recruiter.talent_showcase.ind_instrumentation" }),
      ],
    },
  ];

  const [openSector, setOpenSector] = useState(sectors[0]?.name ?? null);

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
    >
      <Box
        component="h3"
        sx={{
          fontSize: "1.25rem",
          fontFamily: "Segoe UI Bold, system-ui, sans-serif",
          color: "hsl(0, 0%, 98%)",
          mb: "8px",
          mt: 0,
          textAlign: "center",
        }}
      >
        <FormattedMessage id="landing_recruiter.talent_showcase.professions_title" />
      </Box>
      <Box
        component="p"
        sx={{
          color: "hsla(0, 0%, 98%, 0.6)",
          fontSize: "0.9rem",
          textAlign: "center",
          mb: "32px",
          fontFamily: "Segoe UI Regular, system-ui, sans-serif",
        }}
      >
        <FormattedMessage id="landing_recruiter.talent_showcase.professions_subtitle" />
      </Box>

      {/* Mobile + Tablet: Accordion */}
      <Box sx={{ display: { xs: "flex", lg: "none" }, flexDirection: "column", gap: "12px" }}>
        {sectors.map((sector, i) => {
          const Icon = sectorIcons[i];
          const isOpen = openSector === sector.name;

          return (
            <Box
              key={sector.name}
              sx={{
                borderRadius: "1rem",
                border: "1px solid hsla(145, 63%, 42%, 0.25)",
                background: "linear-gradient(to bottom right, hsla(0, 0%, 100%, 0.08), hsla(0, 0%, 100%, 0.03))",
                overflow: "hidden",
              }}
            >
              <Box
                component="button"
                onClick={() => setOpenSector(isOpen ? null : sector.name)}
                aria-expanded={isOpen}
                sx={{
                  width: "100%",
                  px: "24px",
                  py: "16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "12px",
                  bgcolor: "transparent",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <Box
                    sx={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "0.75rem",
                      bgcolor: "hsla(0, 0%, 98%, 0.1)",
                      border: "1px solid hsla(0, 0%, 98%, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon size={18} color="hsl(145, 63%, 42%)" />
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      fontSize: "1.125rem",
                      fontFamily: "Segoe UI Bold, system-ui, sans-serif",
                      color: "hsl(0, 0%, 98%)",
                    }}
                  >
                    {sector.name}
                  </Box>
                </Box>
                <Box
                  sx={{
                    color: "hsl(0, 0%, 98%)",
                    flexShrink: 0,
                    transition: "transform 0.2s ease",
                    transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                  }}
                >
                  <ChevronDown size={18} />
                </Box>
              </Box>

              {isOpen && (
                <Box sx={{ px: "24px", pb: "16px" }}>
                  <Box
                    component="ul"
                    sx={{ listStyle: "none", p: 0, m: 0, display: "flex", flexDirection: "column", gap: "6px" }}
                  >
                    {sector.professions.map((p) => (
                      <Box
                        component="li"
                        key={p}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          fontSize: "0.875rem",
                          color: "hsla(0, 0%, 98%, 0.8)",
                          fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            width: "6px",
                            height: "6px",
                            borderRadius: "9999px",
                            bgcolor: "hsl(145, 63%, 42%)",
                            flexShrink: 0,
                          }}
                        />
                        {p}
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Desktop: Grid */}
      <Box
        sx={{
          display: { xs: "none", lg: "grid" },
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: "24px",
          maxWidth: "1300px",
          mx: "auto",
        }}
      >
        {sectors.map((sector, i) => {
          const Icon = sectorIcons[i];
          return (
            <Box
              key={sector.name}
              sx={{
                borderRadius: "1rem",
                border: "1px solid hsla(145, 63%, 42%, 0.25)",
                background: "linear-gradient(to bottom right, hsla(0, 0%, 100%, 0.08), hsla(0, 0%, 100%, 0.03))",
                p: "24px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  mb: "16px",
                }}
              >
                <Box
                  sx={{
                    width: "36px",
                    height: "36px",
                    borderRadius: "0.75rem",
                    bgcolor: "hsla(0, 0%, 98%, 0.1)",
                    border: "1px solid hsla(0, 0%, 98%, 0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Icon size={18} color="hsl(145, 63%, 42%)" />
                </Box>
                <Box
                  component="h4"
                  sx={{
                    fontSize: "1.125rem",
                    fontFamily: "Segoe UI Bold, system-ui, sans-serif",
                    color: "hsl(0, 0%, 98%)",
                    m: 0,
                  }}
                >
                  {sector.name}
                </Box>
              </Box>
              <Box
                component="ul"
                sx={{ listStyle: "none", p: 0, m: 0, display: "flex", flexDirection: "column", gap: "6px" }}
              >
                {sector.professions.map((p) => (
                  <Box
                    component="li"
                    key={p}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      fontSize: "0.875rem",
                      color: "hsla(0, 0%, 98%, 0.8)",
                      fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        width: "6px",
                        height: "6px",
                        borderRadius: "9999px",
                        bgcolor: "hsl(145, 63%, 42%)",
                        flexShrink: 0,
                      }}
                    />
                    {p}
                  </Box>
                ))}
              </Box>
            </Box>
          );
        })}
      </Box>

      <Box sx={{ textAlign: "center", mt: "32px" }}>
        <Button
          component="a"
          href={`/${i18nLocale}/recruiters/sign_up`}
          sx={{
            bgcolor: "hsl(153 68% 25%)",
            color: "white",
            fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
            fontWeight: 700,
            fontSize: "0.875rem",
            px: "1.5rem",
            py: "0.875rem",
            borderRadius: "0.75rem",
            textTransform: "none",
            display: "inline-flex",
            alignItems: "center",
            gap: "0.5rem",
            textDecoration: "none",
            "&:hover": {
              bgcolor: "hsl(153 68% 25% / 0.9)",
              textDecoration: "none",
            },
          }}
        >
          <FormattedMessage id="landing_recruiter.talent_showcase.create_account" /> <ArrowRight size={14} className="arrow-icon" />
        </Button>
      </Box>
    </Box>
  );
};

const TalentShowcase = ({ candidatesCount = 0 }) => {
  const intl = useIntl();
  const [activeCandidate, setActiveCandidate] = useState(0);

  const dashboardTabs = [
    { label: intl.formatMessage({ id: "landing_recruiter.talent_showcase.tab_dashboard" }), active: false },
    { label: intl.formatMessage({ id: "landing_recruiter.talent_showcase.tab_applications" }), active: false },
    { label: intl.formatMessage({ id: "landing_recruiter.talent_showcase.tab_offers" }), active: false },
    { label: intl.formatMessage({ id: "landing_recruiter.talent_showcase.tab_cv_library" }), active: true },
    { label: intl.formatMessage({ id: "landing_recruiter.talent_showcase.tab_admin" }), active: false },
  ];

  const candidates = [
    {
      avatar: ProfilePicture1,
      name: "Arnaud Lemaire",
      role: intl.formatMessage({ id: "landing_recruiter.talent_showcase.cand1_role" }),
      type: intl.formatMessage({ id: "landing_recruiter.talent_showcase.type_freelance" }),
      exp: intl.formatMessage({ id: "landing_recruiter.talent_showcase.cand1_exp" }),
      location: intl.formatMessage({ id: "landing_recruiter.talent_showcase.cand1_location" }),
      summary: intl.formatMessage({ id: "landing_recruiter.talent_showcase.cand1_summary" }),
    },
    {
      avatar: ProfilePicture2,
      name: "Wim Peeters",
      role: intl.formatMessage({ id: "landing_recruiter.talent_showcase.cand2_role" }),
      type: intl.formatMessage({ id: "landing_recruiter.talent_showcase.type_permanent" }),
      exp: intl.formatMessage({ id: "landing_recruiter.talent_showcase.cand2_exp" }),
      location: intl.formatMessage({ id: "landing_recruiter.talent_showcase.cand2_location" }),
      summary: intl.formatMessage({ id: "landing_recruiter.talent_showcase.cand2_summary" }),
    },
    {
      avatar: ProfilePicture4,
      name: "Sophie Renard",
      role: intl.formatMessage({ id: "landing_recruiter.talent_showcase.cand3_role" }),
      type: intl.formatMessage({ id: "landing_recruiter.talent_showcase.type_permanent" }),
      exp: intl.formatMessage({ id: "landing_recruiter.talent_showcase.cand3_exp" }),
      location: intl.formatMessage({ id: "landing_recruiter.talent_showcase.cand3_location" }),
      summary: intl.formatMessage({ id: "landing_recruiter.talent_showcase.cand3_summary" }),
    },
    {
      avatar: ProfilePicture3,
      name: "Bram Desmet",
      role: intl.formatMessage({ id: "landing_recruiter.talent_showcase.cand4_role" }),
      type: intl.formatMessage({ id: "landing_recruiter.talent_showcase.type_freelance" }),
      exp: intl.formatMessage({ id: "landing_recruiter.talent_showcase.cand4_exp" }),
      location: intl.formatMessage({ id: "landing_recruiter.talent_showcase.cand4_location" }),
      summary: intl.formatMessage({ id: "landing_recruiter.talent_showcase.cand4_summary" }),
    },
    {
      avatar: ProfilePicture5,
      name: "Maxime Laurent",
      role: intl.formatMessage({ id: "landing_recruiter.talent_showcase.cand5_role" }),
      type: intl.formatMessage({ id: "landing_recruiter.talent_showcase.type_permanent" }),
      exp: intl.formatMessage({ id: "landing_recruiter.talent_showcase.cand5_exp" }),
      location: intl.formatMessage({ id: "landing_recruiter.talent_showcase.cand5_location" }),
      summary: intl.formatMessage({ id: "landing_recruiter.talent_showcase.cand5_summary" }),
    },
    {
      avatar: ProfilePicture3,
      name: "Jonas Mertens",
      role: intl.formatMessage({ id: "landing_recruiter.talent_showcase.cand6_role" }),
      type: intl.formatMessage({ id: "landing_recruiter.talent_showcase.type_permanent" }),
      exp: intl.formatMessage({ id: "landing_recruiter.talent_showcase.cand6_exp" }),
      location: intl.formatMessage({ id: "landing_recruiter.talent_showcase.cand6_location" }),
      summary: intl.formatMessage({ id: "landing_recruiter.talent_showcase.cand6_summary" }),
    },
    {
      avatar: ProfilePicture4,
      name: "Manon Dubois",
      role: intl.formatMessage({ id: "landing_recruiter.talent_showcase.cand7_role" }),
      type: intl.formatMessage({ id: "landing_recruiter.talent_showcase.type_freelance" }),
      exp: intl.formatMessage({ id: "landing_recruiter.talent_showcase.cand7_exp" }),
      location: intl.formatMessage({ id: "landing_recruiter.talent_showcase.cand7_location" }),
      summary: intl.formatMessage({ id: "landing_recruiter.talent_showcase.cand7_summary" }),
    },
    {
      avatar: ProfilePicture2,
      name: "Thibaut Maes",
      role: intl.formatMessage({ id: "landing_recruiter.talent_showcase.cand8_role" }),
      type: intl.formatMessage({ id: "landing_recruiter.talent_showcase.type_freelance" }),
      exp: intl.formatMessage({ id: "landing_recruiter.talent_showcase.cand8_exp" }),
      location: intl.formatMessage({ id: "landing_recruiter.talent_showcase.cand8_location" }),
      summary: intl.formatMessage({ id: "landing_recruiter.talent_showcase.cand8_summary" }),
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveCandidate((prev) => (prev + 1) % candidates.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Box
      component="section"
      sx={{
        py: { xs: "32px", md: "48px" },
        px: { xs: "16px", sm: "24px", lg: "32px" },
        bgcolor: "hsl(178, 93%, 10%)",
      }}
    >
      <Box sx={{ maxWidth: "1152px", mx: "auto" }}>
        {/* Heading */}
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          sx={{ textAlign: "center", mb: "48px" }}
        >
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              px: "16px",
              py: "6px",
              borderRadius: "9999px",
              bgcolor: "hsla(145, 63%, 42%, 0.2)",
              border: "1px solid hsla(145, 63%, 42%, 0.3)",
              color: "hsl(145, 63%, 42%)",
              fontSize: "0.75rem",
              fontFamily: "Segoe UI Bold, system-ui, sans-serif",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              mb: "16px",
            }}
          >
            <Database size={14} />
            <FormattedMessage id="landing_recruiter.talent_showcase.badge" />
          </Box>
          <Box
            component="h2"
            sx={{
              fontSize: { xs: "1.875rem", md: "2.25rem" },
              fontFamily: "Segoe UI Black, system-ui, sans-serif",
              color: "hsl(0, 0%, 98%)",
              letterSpacing: "-0.025em",
              mb: "16px",
              mt: 0,
            }}
          >
            <FormattedMessage id="landing_recruiter.talent_showcase.title" />
          </Box>
          <Box
            component="p"
            sx={{
              color: "hsla(0, 0%, 98%, 0.7)",
              fontSize: "1.125rem",
              maxWidth: "672px",
              mx: "auto",
              textAlign: "center",
              fontFamily: "Segoe UI Regular, system-ui, sans-serif",
            }}
          >
            <FormattedMessage id="landing_recruiter.talent_showcase.subtitle" />
          </Box>
        </Box>

        {/* CV-thèque dashboard mockup */}
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          sx={{ mb: "48px" }}
        >
          <Box
            sx={{
              borderRadius: "1rem",
              border: "1px solid hsl(220, 13%, 91%)",
              bgcolor: "hsl(0, 0%, 100%)",
              boxShadow:
                "0 20px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)",
              overflow: "hidden",
            }}
          >
            {/* Top nav tabs */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                borderBottom: "1px solid hsl(220, 13%, 91%)",
                px: "24px",
                overflow: "hidden",
              }}
            >
              {dashboardTabs.map((tab) => (
                <Box
                  component="span"
                  key={tab.label}
                  sx={{
                    px: "16px",
                    py: "12px",
                    whiteSpace: "nowrap",
                    fontSize: "0.75rem",
                    fontFamily: "Segoe UI Bold, system-ui, sans-serif",
                    color: tab.active
                      ? "hsl(145, 63%, 42%)"
                      : "hsl(220, 10%, 46%)",
                    borderBottom: tab.active
                      ? "2px solid hsl(145, 63%, 42%)"
                      : "2px solid transparent",
                  }}
                >
                  {tab.label}
                </Box>
              ))}
            </Box>

            {/* Search bar */}
            <Box sx={{ px: "16px", pt: "12px", pb: "8px" }}>
              <Box sx={{ display: "flex", gap: "8px" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flex: 1,
                    gap: "8px",
                    px: "12px",
                    py: "6px",
                    borderRadius: "12px",
                    bgcolor: "hsl(220, 14%, 96%)",
                    border: "1px solid hsl(220, 13%, 91%)",
                  }}
                >
                  <Search size={11} color="hsl(220, 10%, 46%)" />
                  <Box
                    component="span"
                    sx={{
                      fontSize: "9px",
                      color: "hsl(220, 10%, 46%)",
                      fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                    }}
                  >
                    {intl.formatMessage({ id: "landing_recruiter.talent_showcase.search_placeholder" })}
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    px: "12px",
                    py: "6px",
                    borderRadius: "12px",
                    bgcolor: "hsl(220, 14%, 96%)",
                    border: "1px solid hsl(220, 13%, 91%)",
                  }}
                >
                  <MapPin size={10} color="hsl(220, 10%, 46%)" />
                  <Box
                    component="span"
                    sx={{
                      fontSize: "9px",
                      color: "hsl(220, 10%, 46%)",
                      fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                    }}
                  >
                    {intl.formatMessage({ id: "landing_recruiter.talent_showcase.location_placeholder" })}
                  </Box>
                </Box>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "center", mt: "8px" }}>
                <Box
                  component="span"
                  sx={{
                    px: "16px",
                    py: "4px",
                    borderRadius: "9999px",
                    bgcolor: "hsl(145, 63%, 42%)",
                    color: "hsl(0, 0%, 100%)",
                    fontSize: "9px",
                    fontFamily: "Segoe UI Bold, system-ui, sans-serif",
                  }}
                >
                  {intl.formatMessage({ id: "landing_recruiter.talent_showcase.advanced_filters" })}
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "6px",
                  mt: "8px",
                }}
              >
                <Box
                  component="span"
                  sx={{
                    fontSize: "10px",
                    fontFamily: "Segoe UI Bold, system-ui, sans-serif",
                    color: "hsla(220, 25%, 12%, 0.7)",
                  }}
                >
                  {intl.formatMessage({ id: "landing_recruiter.talent_showcase.candidates_label" })}
                </Box>
                <Box
                  component="span"
                  sx={{
                    fontSize: "10px",
                    fontFamily: "Segoe UI Bold, system-ui, sans-serif",
                    color: "hsl(145, 63%, 42%)",
                    bgcolor: "hsla(145, 63%, 42%, 0.15)",
                    px: "8px",
                    py: "2px",
                    borderRadius: "9999px",
                  }}
                >
                  {formatNumber(candidatesCount)}
                </Box>
              </Box>
            </Box>

            {/* Main content: split list + detail */}
            <Box
              sx={{
                display: "flex",
                borderTop: "1px solid hsl(220, 13%, 91%)",
                height: "520px",
              }}
            >
              {/* Left – candidate list */}
              <Box
                sx={{
                  width: "45%",
                  borderRight: "1px solid hsl(220, 13%, 91%)",
                  overflowY: "auto",
                }}
              >
                {candidates.map((c, i) => (
                  <Box
                    component={motion.div}
                    key={i}
                    animate={{
                      backgroundColor:
                        i === activeCandidate
                          ? "hsl(220, 14%, 96%)"
                          : "transparent",
                    }}
                    transition={{ duration: 0.4 }}
                    onClick={() => setActiveCandidate(i)}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "8px",
                      px: "12px",
                      py: "8px",
                      borderBottom: "1px solid hsl(220, 13%, 91%)",
                      cursor: "pointer",
                    }}
                  >
                    <Box
                      component="img"
                      src={c.avatar}
                      alt={c.name}
                      sx={{
                        width: "28px",
                        height: "28px",
                        borderRadius: "9999px",
                        objectFit: "cover",
                        flexShrink: 0,
                        mt: "2px",
                      }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        component="p"
                        sx={{
                          fontSize: "10px",
                          fontFamily: "Segoe UI Bold, system-ui, sans-serif",
                          color: "hsl(220, 25%, 12%)",
                          m: 0,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {c.name}
                      </Box>
                      <Box
                        component="p"
                        sx={{
                          fontSize: "8px",
                          color: "hsl(220, 10%, 46%)",
                          m: 0,
                          fontFamily:
                            "Segoe UI Regular, system-ui, sans-serif",
                        }}
                      >
                        {c.role}
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          mt: "2px",
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            fontSize: "7px",
                            color: "hsl(220, 10%, 46%)",
                            bgcolor: "hsl(220, 14%, 96%)",
                            px: "6px",
                            py: "2px",
                            borderRadius: "4px",
                            fontFamily:
                              "Segoe UI Regular, system-ui, sans-serif",
                          }}
                        >
                          {c.type}
                        </Box>
                        <Box
                          component="span"
                          sx={{
                            fontSize: "7px",
                            color: "hsl(220, 10%, 46%)",
                            fontFamily:
                              "Segoe UI Regular, system-ui, sans-serif",
                          }}
                        >
                          {c.exp}
                        </Box>
                        <Box
                          component="span"
                          sx={{
                            fontSize: "7px",
                            color: "hsl(220, 10%, 46%)",
                            fontFamily:
                              "Segoe UI Regular, system-ui, sans-serif",
                          }}
                        >
                          {c.location}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>

              {/* Right – candidate detail */}
              <Box
                sx={{
                  width: "55%",
                  overflow: "hidden",
                  p: "12px",
                  position: "relative",
                }}
              >
                <AnimatePresence mode="wait">
                  <Box
                    component={motion.div}
                    key={activeCandidate}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.35, ease: "easeOut" }}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                      position: "absolute",
                      top: "12px",
                      left: "12px",
                      right: "12px",
                    }}
                  >
                    {/* Profile header */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <Box
                        component="img"
                        src={candidates[activeCandidate].avatar}
                        alt={candidates[activeCandidate].name}
                        sx={{
                          width: "36px",
                          height: "36px",
                          borderRadius: "9999px",
                          objectFit: "cover",
                        }}
                      />
                      <Box>
                        <Box
                          component="p"
                          sx={{
                            fontSize: "11px",
                            fontFamily:
                              "Segoe UI Bold, system-ui, sans-serif",
                            color: "hsl(220, 25%, 12%)",
                            m: 0,
                          }}
                        >
                          {candidates[activeCandidate].name}
                        </Box>
                        <Box
                          component="p"
                          sx={{
                            fontSize: "9px",
                            color: "hsl(220, 10%, 46%)",
                            m: 0,
                            fontFamily:
                              "Segoe UI Regular, system-ui, sans-serif",
                          }}
                        >
                          {candidates[activeCandidate].role}
                        </Box>
                      </Box>
                    </Box>

                    {/* Tags */}
                    <Box sx={{ display: "flex", gap: "6px" }}>
                      <Box
                        component="span"
                        sx={{
                          fontSize: "8px",
                          fontFamily:
                            "Segoe UI Bold, system-ui, sans-serif",
                          px: "8px",
                          py: "2px",
                          borderRadius: "9999px",
                          border: "1px solid hsla(145, 63%, 42%, 0.3)",
                          color: "hsl(145, 63%, 42%)",
                        }}
                      >
                        {candidates[activeCandidate].location}
                      </Box>
                      <Box
                        component="span"
                        sx={{
                          fontSize: "8px",
                          fontFamily:
                            "Segoe UI Bold, system-ui, sans-serif",
                          px: "8px",
                          py: "2px",
                          borderRadius: "9999px",
                          border: "1px solid hsla(145, 63%, 42%, 0.3)",
                          color: "hsl(145, 63%, 42%)",
                        }}
                      >
                        {candidates[activeCandidate].exp}
                      </Box>
                    </Box>

                    {/* Coordonnées */}
                    <Box
                      sx={{
                        borderRadius: "8px",
                        bgcolor: "hsl(220, 14%, 96%)",
                        border: "1px solid hsl(220, 13%, 91%)",
                        p: "8px",
                      }}
                    >
                      <Box
                        component="p"
                        sx={{
                          fontSize: "9px",
                          fontFamily:
                            "Segoe UI Bold, system-ui, sans-serif",
                          color: "hsl(220, 25%, 12%)",
                          mb: "4px",
                          mt: 0,
                        }}
                      >
                        {intl.formatMessage({ id: "landing_recruiter.talent_showcase.contact_info" })}
                      </Box>
                      <Box
                        component="p"
                        sx={{
                          fontSize: "8px",
                          color: "hsl(220, 10%, 46%)",
                          m: 0,
                          fontFamily:
                            "Segoe UI Regular, system-ui, sans-serif",
                        }}
                      >
                        {candidates[activeCandidate].name
                          .toLowerCase()
                          .replace(" ", ".")}
                        @example.com
                      </Box>
                      <Box
                        component="p"
                        sx={{
                          fontSize: "8px",
                          color: "hsl(220, 10%, 46%)",
                          m: 0,
                          fontFamily:
                            "Segoe UI Regular, system-ui, sans-serif",
                        }}
                      >
                        +32 321{" "}
                        {String(12 + activeCandidate * 11).padStart(2, "0")}{" "}
                        {String(34 + activeCandidate * 7).padStart(2, "0")}{" "}
                        {String(56 + activeCandidate * 3).padStart(2, "0")}
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          gap: "12px",
                          mt: "4px",
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            fontSize: "7px",
                            color: "hsl(220, 10%, 46%)",
                            fontFamily:
                              "Segoe UI Regular, system-ui, sans-serif",
                          }}
                        >
                          {intl.formatMessage({ id: "landing_recruiter.talent_showcase.region" })} : {candidates[activeCandidate].location}
                        </Box>
                        <Box
                          component="span"
                          sx={{
                            fontSize: "7px",
                            color: "hsl(220, 10%, 46%)",
                            fontFamily:
                              "Segoe UI Regular, system-ui, sans-serif",
                          }}
                        >
                          {intl.formatMessage({ id: "landing_recruiter.talent_showcase.experience" })} : {candidates[activeCandidate].exp}
                        </Box>
                      </Box>
                    </Box>

                    {/* Préférences */}
                    <Box
                      sx={{
                        borderRadius: "8px",
                        bgcolor: "hsl(220, 14%, 96%)",
                        border: "1px solid hsl(220, 13%, 91%)",
                        p: "8px",
                      }}
                    >
                      <Box
                        component="p"
                        sx={{
                          fontSize: "9px",
                          fontFamily:
                            "Segoe UI Bold, system-ui, sans-serif",
                          color: "hsl(220, 25%, 12%)",
                          mb: "4px",
                          mt: 0,
                        }}
                      >
                        {intl.formatMessage({ id: "landing_recruiter.talent_showcase.preferences" })}
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "2px",
                        }}
                      >
                        <Box
                          component="p"
                          sx={{
                            fontSize: "8px",
                            color: "hsl(220, 10%, 46%)",
                            m: 0,
                            fontFamily:
                              "Segoe UI Regular, system-ui, sans-serif",
                          }}
                        >
                          {intl.formatMessage({ id: "landing_recruiter.talent_showcase.job_label" })} : {candidates[activeCandidate].role}
                        </Box>
                        <Box
                          component="p"
                          sx={{
                            fontSize: "8px",
                            color: "hsl(220, 10%, 46%)",
                            m: 0,
                            fontFamily:
                              "Segoe UI Regular, system-ui, sans-serif",
                          }}
                        >
                          {intl.formatMessage({ id: "landing_recruiter.talent_showcase.region" })} : {candidates[activeCandidate].location}
                        </Box>
                        <Box
                          component="p"
                          sx={{
                            fontSize: "8px",
                            color: "hsl(220, 10%, 46%)",
                            m: 0,
                            fontFamily:
                              "Segoe UI Regular, system-ui, sans-serif",
                          }}
                        >
                          {intl.formatMessage({ id: "landing_recruiter.talent_showcase.type_label" })} : {candidates[activeCandidate].type}
                        </Box>
                      </Box>
                      <Box
                        component="span"
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px",
                          mt: "6px",
                          fontSize: "8px",
                          fontFamily:
                            "Segoe UI Bold, system-ui, sans-serif",
                          color: "hsl(145, 63%, 42%)",
                          bgcolor: "hsla(145, 63%, 42%, 0.1)",
                          px: "8px",
                          py: "2px",
                          borderRadius: "4px",
                        }}
                      >
                        <Download size={8} /> {intl.formatMessage({ id: "landing_recruiter.talent_showcase.download_cv" })}
                      </Box>
                    </Box>

                    {/* Résumé */}
                    <Box
                      sx={{
                        borderRadius: "8px",
                        bgcolor: "hsl(220, 14%, 96%)",
                        border: "1px solid hsl(220, 13%, 91%)",
                        p: "8px",
                      }}
                    >
                      <Box
                        component="p"
                        sx={{
                          fontSize: "9px",
                          fontFamily:
                            "Segoe UI Bold, system-ui, sans-serif",
                          color: "hsl(220, 25%, 12%)",
                          mb: "4px",
                          mt: 0,
                        }}
                      >
                        {intl.formatMessage({ id: "landing_recruiter.talent_showcase.summary_label" })}
                      </Box>
                      <Box
                        component="p"
                        sx={{
                          fontSize: "8px",
                          color: "hsl(220, 10%, 46%)",
                          lineHeight: 1.625,
                          m: 0,
                          fontFamily:
                            "Segoe UI Regular, system-ui, sans-serif",
                        }}
                      >
                        {candidates[activeCandidate].summary}
                      </Box>
                    </Box>
                  </Box>
                </AnimatePresence>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box sx={{ textAlign: "center", mb: "64px" }}>
          <Button
            component="a"
            href={"recruiters/sign_up"}
            sx={{
              bgcolor: "hsl(153 68% 25%)",
              color: "white",
              fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
              fontWeight: 700,
              fontSize: "0.875rem",
              px: "1.5rem",
              py: "0.875rem",
              borderRadius: "0.75rem",
              textTransform: "none",
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              textDecoration: "none",
              "&:hover": {
                bgcolor: "hsl(153 68% 25% / 0.9)",
                textDecoration: "none",
              },
            }}
          >
            <FormattedMessage id="landing_recruiter.talent_showcase.cta" /> <ArrowRight size={14} className="arrow-icon" />
          </Button>
        </Box>

        {/* Professions grid */}
        <ProfessionsGrid />
      </Box>
      
    </Box>
  );
};

export default TalentShowcase;
